import * as React from 'react'
import { EllipsisVertical, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useDeletePortalContact } from '@/hooks/use-portal'
import { showSuccess } from '@/lib/toast'
import { ContactDeleteDialog } from '@/components/custom/contacts/contact-delete-dialog'
import { PortalAddContactModal } from './portal-add-contact-modal'
import type { PortalContact } from '@/services/portal.service'
import { formatContactName } from '@/lib/contacts/format-contact-name'

interface PortalContactActionsMenuProps {
  contact: PortalContact
  onChanged?: () => void
  className?: string
}

export function PortalContactActionsMenu({
  contact,
  onChanged,
  className,
}: PortalContactActionsMenuProps) {
  const [isEditOpen, setIsEditOpen] = React.useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false)

  const deleteContact = useDeletePortalContact()
  const isPending = deleteContact.isPending

  const handleDelete = async () => {
    try {
      await deleteContact.mutateAsync(contact.id)
      showSuccess('Contact removed from company successfully')
      setIsDeleteOpen(false)
      onChanged?.()
    } catch {
      // Error toast is shown by the axios interceptor — swallow here so the
      // dialog stays open without firing a duplicate notification.
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className={
              className ?? 'h-6 w-6 text-primary/70 hover:text-primary'
            }
            aria-label={`Open actions for ${formatContactName(contact)}`}
          >
            <EllipsisVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem onClick={() => setIsEditOpen(true)}>
            <Pencil className="size-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setIsDeleteOpen(true)}
            className="text-destructive hover:text-destructive focus:text-destructive"
          >
            <Trash2 className="size-4" />
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <PortalAddContactModal
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        mode="edit"
        contact={contact}
        onSuccess={onChanged}
      />

      <ContactDeleteDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title="Remove Contact"
        description={`Are you sure you want to remove "${formatContactName(contact)}" from this company?`}
        confirmLabel="Remove"
        isPending={isPending}
        onConfirm={handleDelete}
      />
    </>
  )
}
